import { useNavigate } from '@tanstack/react-router'
import { EventCard } from '@/entities/event/ui'
import { Skeleton } from '@/shared/ui/skeleton'
import { useTelegram } from '@/shared/lib/telegram'
import type { Event } from '@/entities/event/model'

export interface EventsListProps {
  /**
   * Список событий для отображения
   */
  events?: Event[]
  /**
   * Состояние загрузки
   * @default false
   */
  loading?: boolean
  /**
   * Сообщение при пустом списке
   * @default "События не найдены"
   */
  emptyMessage?: string
}

/**
 * Виджет: Список событий
 * 
 * Композитный компонент для отображения списка событий.
 * Использует EventCard из entities/event/ui и добавляет навигацию.
 * 
 * @example
 * ```tsx
 * <EventsList
 *   events={events}
 *   loading={isLoading}
 * />
 * ```
 */
export function EventsList({
  events,
  loading = false,
  emptyMessage = 'События не найдены',
}: EventsListProps) {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()

  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="w-full h-64 rounded-lg" />
        ))}
      </div>
    )
  }

  if (!events || events.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-base text-gray-500 dark:text-gray-400">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {events.map((event) => (
        <EventCard
          key={event.id}
          event={event}
          onClick={() => {
            hapticFeedback.impactOccurred('light')
            navigate({ to: '/event/$eventId', params: { eventId: event.id } })
          }}
          onHapticFeedback={(type) => hapticFeedback.impactOccurred(type || 'light')}
        />
      ))}
    </div>
  )
}


